import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { moderationApi, UserReport } from '../../services/moderationApi';
import { CONTEXT_LABELS, STATUS_COLORS, STATUS_LABELS, TYPE_LABELS, getInitials } from './moderation/labels';
import { ReportDetails } from './moderation/ReportDetails';

/**
 * Reports filed by users, for Kizu staff. The email sent for each new report
 * links here with ?report=<id>, which opens that report whatever its status.
 */
export const ModerationDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const linkedReportId = searchParams.get('report');
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(linkedReportId ? '' : 'pending');
  const [selectedId, setSelectedId] = useState<string | null>(linkedReportId);

  const loadReports = useCallback(async () => {
    setError(null);
    const result = await moderationApi.getReports(statusFilter || undefined);
    if (result.success && result.data) setReports(result.data.reports || []);
    else setError(result.error || 'Failed to load reports');
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    setLoading(true);
    loadReports();
  }, [loadReports]);

  const selectedReport = reports.find(report => report.id === selectedId) || null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Moderation</h2>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All reports</option>
          <option value="pending">Pending</option>
          <option value="action_taken">Action taken</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Reports ({reports.length})</h3>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-500">Loading…</div>
            ) : reports.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No reports found</div>
            ) : (
              reports.map(report => (
                <div
                  key={report.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedId === report.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => setSelectedId(report.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-600 font-medium text-sm">
                        {getInitials(report.reported_user?.full_name)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 truncate">
                          {report.reported_user?.full_name || 'Unknown user'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[report.status]}`}>
                          {STATUS_LABELS[report.status]}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {TYPE_LABELS[report.report_type]} • {CONTEXT_LABELS[report.report_context]}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Reported by {report.reporter?.full_name || 'Unknown user'} •{' '}
                        {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <ReportDetails report={selectedReport} onChanged={loadReports} />
      </div>
    </div>
  );
};
